// src/App.js
import React, { useContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { useEffect } from "react";
import { apiRequest } from "./services/api";
import { LanguageContext } from "./context/LanguageContext";
import HomePage from "./pages/HomePage";
import CartPage from "./pages/CartPage";
import WishlistPage from "./pages/WishlistPage";
import CheckoutPage from "./pages/CheckoutPage";
import ThankYouPage from "./pages/ThankYouPage";
import AccountPage from "./pages/AccountPage";
import OrdersPage from "./pages/OrdersPage";
import AboutPage from "./pages/AboutPage";
import PaymentDeliveryPage from "./pages/PaymentDeliveryPage";
import ReturnsPage from "./pages/ReturnsPage";
import CreateAccountPage from "./pages/CreateAccountPage";
import EditProfilePage from "./pages/EditProfilePage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailsPage from "./pages/ProductDetailsPage";
import AllProductsPage from "./pages/AllProductsPage";
import RequireAuth from "./components/RequireAuth";
import ContactPage from "./pages/ContactPage";
import ChatPage from "./pages/ChatPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import AdminProductAnalytics from "./admin/pages/AdminProductAnalytics";
import AdminLayout from "./admin/AdminLayout";
import AdminLogin from "./admin/AdminLogin";
import AdminDashboard from "./admin/AdminDashboard";
import ProtectedAdminRoute from "./admin/ProtectedAdminRoute";
import AdminAddOrders from "./admin/pages/AdminAddOrders";
import AdminDiscounts from "./admin/pages/AdminDiscounts";
import AdminCreateDiscount from "./admin/pages/AdminCreateDiscount";
import AdminCreateBulkDiscount from "./admin/pages/AdminCreateBulkDiscount";
import AdminProducts from "./admin/pages/AdminProducts";
import AdminImportProducts from "./admin/pages/AdminImportProducts";
import AdminAddProduct from "./admin/pages/AdminAddProduct";
import AdminEditProduct from "./admin/pages/AdminEditProduct";
import AdminOrders from "./admin/pages/AdminOrders";
import AdminOrderDetails from "./admin/pages/AdminOrderDetails";
import AdminUsers from "./admin/pages/AdminUsers";
import AdminCustomers from "./admin/pages/AdminCustomers";
import AdminCustomerProfile from "./admin/pages/AdminCustomerProfile";
import AdminAnalytics from "./admin/pages/AdminAnalytics";
import AdminSettings from "./admin/pages/AdminSettings";
import AdminNotifications from "./admin/pages/AdminNotifications";
import AdminInventory from "./admin/pages/AdminInventory";
import AdminMessages from "./admin/pages/AdminMessages";
import AdminMessageView from "./admin/pages/AdminMessageView";
import AdminEmailTemplates from "./admin/pages/AdminEmailTemplates";
import AdminEmailEditor from "./admin/pages/AdminEmailEditor";
import AdminSeoManager from "./admin/pages/AdminSeoManager";
import AdminTopBanner from "./admin/pages/AdminTopBanner";
import AdminCategories from "./admin/pages/AdminCategories";
import AdminSaleBanner from "./admin/pages/AdminSaleBanner";
import AdminShippingSettings from "./admin/pages/AdminShippingSettings";
import AdminNewInProducts from "./admin/pages/AdminNewInProducts";
import AdminBestsellerProducts from "./admin/pages/AdminBestsellerProducts";
import AdminSaleProducts from "./admin/pages/AdminSaleProducts";
import AdminAbandonedCarts  from "./admin/pages/AdminAbandonedCarts";
import AdminUserActivity from "./admin/pages/AdminUserActivity";
import AdminEmailTemplateManager from "./admin/pages/AdminEmailTemplateManager";
import AdminMakeSale from "./admin/pages/AdminMakeSale";
import AdminRemoveSale from "./admin/pages/AdminRemoveSale";
import AdminCreateBXGYDiscount from "./admin/pages/AdminCreateBXGYDiscount";
import AdminAddCustomer from "./admin/pages/AdminAddCustomer";
import AdminCreateOrderDiscount from "./admin/pages/AdminCreateOrderDiscount";
import AdminCreateDiscountCode from "./admin/pages/AdminCreateDiscountCode";
import AdminAbandonedCartDetails from "./admin/pages/AdminAbandonedCartDetails";
import AdminCreateShippingDiscount from "./admin/pages/AdminCreateShippingDiscount";
import AdminSegments from "./admin/pages/AdminSegments";
import AdminReports from "./admin/pages/AdminReports";

function LandingPageTracker() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;

    const alreadyTracked = sessionStorage.getItem("esill-landing-page-tracked");
    if (alreadyTracked) return;

    const landingPage =
      `${location.pathname}${location.search}` || "/";

    const getLandingLabel = (path) => {
      if (path === "/") return "Home";
      if (path.startsWith("/product/")) return "Product page";
      if (path.startsWith("/products")) return "Products";
      if (path.startsWith("/all-products")) return "All products";
      if (path.startsWith("/cart")) return "Cart";
      if (path.startsWith("/checkout")) return "Checkout";
      if (path.startsWith("/wishlist")) return "Wishlist";
      if (path.startsWith("/contact")) return "Contact";
      if (path.startsWith("/create-account")) return "Create account";
      if (path.startsWith("/account")) return "Account";
      return "Other";
    };

    const params = new URLSearchParams(location.search);

    let referrerUrl = document.referrer || "";
    let referrerDomain = "";

    try {
      if (referrerUrl) {
        const refUrl = new URL(referrerUrl);

        if (refUrl.hostname !== window.location.hostname) {
          referrerDomain = refUrl.hostname.replace(/^www\./, "");
        } else {
          referrerUrl = "";
        }
      }
    } catch {
      referrerUrl = "";
      referrerDomain = "";
    }

    sessionStorage.setItem("esill-landing-page-tracked", "1");

    apiRequest("/analytics/track", {
      method: "POST",
      body: JSON.stringify({
        action_type: "set_landing_page",
        entity_type: "session",
        landing_page: landingPage,
        landing_page_label: getLandingLabel(location.pathname),
        referrer_url: referrerUrl || null,
        referrer_domain: referrerDomain || null,
        utm_source: params.get("utm_source") || null,
        utm_medium: params.get("utm_medium") || null,
        utm_campaign: params.get("utm_campaign") || null,
      }),
    }).catch(() => {});
  }, [location.pathname, location.search]);

  return null;
}

function LanguageUrlSync() {
  const location = useLocation();
  const { lang, setLanguage } = useContext(LanguageContext);

  // URL -> language
  // Runs only when URL changes, not when lang changes.
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;

    const params = new URLSearchParams(location.search);
    const urlLang = String(params.get("lang") || "").toUpperCase();

    if (urlLang === "EN" || urlLang === "AR") {
      setLanguage(urlLang);
    }
  }, [location.pathname, location.search, setLanguage]);

  // language -> URL
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;

    const params = new URLSearchParams(location.search);
    const currentLang = String(params.get("lang") || "").toUpperCase();

    if (currentLang === lang) return;

    params.set("lang", lang);

    const nextSearch = params.toString();
    const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;

    window.history.replaceState(window.history.state, "", nextUrl);
  }, [lang, location.pathname, location.search]);

  return null;
}

function NavigationScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;

    let saved = null;

    try {
      saved = JSON.parse(
        sessionStorage.getItem("etika-product-return-positionn") || "null"
      );
    } catch {
      saved = null;
    }

    const currentKey = `${location.pathname}${location.search}`;

    // When returning from product details to the saved source page,
    // do not force top. The source page will restore itself after data loads.
    if (saved?.sourceKey === currentKey) return;

    // Fresh navigation should open from top.
    if (navigationType === "PUSH") {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    }
  }, [location.pathname, location.search, location.key, navigationType]);

  return null;
}


function PublicThemeSync() {
  const location = useLocation();

  useEffect(() => {
    const isAdmin =
      location.pathname.startsWith("/admin");

    document.documentElement.classList.toggle(
      "chiaca-public-site",
      !isAdmin
    );

    document.body.classList.toggle(
      "chiaca-public-site",
      !isAdmin
    );
  }, [location.pathname]);

  return null;
}


function App() {
  return (
    <Router>
  <PublicThemeSync />
    <LanguageUrlSync />
  <LandingPageTracker />
  <NavigationScrollManager />
  <Routes>
        {/* ================== CLIENT ================== */}
        <Route path="/" element={<HomePage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/payment-delivery" element={<PaymentDeliveryPage />} />
<Route path="/returns" element={<ReturnsPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/thank-you" element={<ThankYouPage />} />

        <Route
          path="/account"
          element={
            <RequireAuth>
              <AccountPage />
            </RequireAuth>
          }
        />

        <Route
          path="/orders"
          element={
            <RequireAuth>
              <OrdersPage />
            </RequireAuth>
          }
        />

        <Route path="/create-account" element={<CreateAccountPage />} />
        <Route path="/edit-profile" element={<EditProfilePage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/product/:slug" element={<ProductDetailsPage />} />
        <Route path="/all-products" element={<AllProductsPage />} />

        {/* ================== ADMIN AREA (ALL PROTECTED + ADMIN LANG) ================== */}
<Route path="/admin/login" element={<AdminLogin />} />

<Route
  path="/admin"
  element={
    <ProtectedAdminRoute>
      <AdminLayout />
    </ProtectedAdminRoute>
  }
>
  <Route path="dashboard" element={<AdminDashboard />} />
  <Route path="analytics/products" element={<AdminProductAnalytics />} />
  <Route path="products" element={<AdminProducts />} />
<Route
  path="products/import"
  element={<AdminImportProducts />}
/>
  <Route path="products/add" element={<AdminAddProduct />} />
  <Route path="products/edit/:id" element={<AdminEditProduct />} />
  <Route path="discounts" element={<AdminDiscounts />} />
  <Route path="discounts/create-product-discount" element={<AdminCreateDiscount />} />
  <Route path="discounts/create-bxgy-discount"  element={<AdminCreateBXGYDiscount />} />
  <Route path="discounts/create-bxgy-discount/:id" element={<AdminCreateBXGYDiscount />} />
  <Route path="discounts/edit/:id" element={<AdminCreateDiscount />} />
  <Route path="discounts/create-bulk-discount" element={<AdminCreateBulkDiscount />} />
  <Route path="discounts/create-order-discount" element={<AdminCreateOrderDiscount />} />
<Route path="discounts/create-order-discount/:id" element={<AdminCreateOrderDiscount />} />
<Route path="discounts/create-discount-code" element={<AdminCreateDiscountCode />} />
<Route path="discounts/create-discount-code/:id" element={<AdminCreateDiscountCode />} />
 <Route path="discounts/create-shipping-discount" element={<AdminCreateShippingDiscount />} />
<Route path="discounts/create-shipping-discount/:id" element={<AdminCreateShippingDiscount />}/>
  <Route path="categories" element={<AdminCategories />} />
  <Route path="segments" element={<AdminSegments />} />
  <Route path="orders" element={<AdminOrders />} />
  <Route path="orders/:id" element={<AdminOrderDetails />} />
  <Route path="orders/add" element={<AdminAddOrders />} />

  <Route path="users" element={<AdminUsers />} />
  <Route path="customers" element={<AdminCustomers />} />
  <Route path="customers/add" element={<AdminAddCustomer />} />
  <Route path="customers/:id" element={<AdminCustomerProfile />} />

  <Route path="analytics" element={<AdminAnalytics />} />
  <Route path="reports" element={<AdminReports />} />
  <Route path="settings" element={<AdminSettings />} />

  <Route path="notifications" element={<AdminNotifications />} />
  <Route path="inventory" element={<AdminInventory />} />

  <Route path="messages" element={<AdminMessages />} />
  <Route path="messages/:id" element={<AdminMessageView />} />

  <Route path="email-templates" element={<AdminEmailTemplates />} />
  <Route path="email-templates/:key" element={<AdminEmailEditor />} />

  <Route path="seo" element={<AdminSeoManager />} />
  <Route path="top-banner" element={<AdminTopBanner />} />
  <Route path="sale-banner" element={<AdminSaleBanner />} />
  <Route path="shipping" element={<AdminShippingSettings />} />

  <Route path="products/new-in" element={<AdminNewInProducts />} />
  <Route path="products/bestsellers" element={<AdminBestsellerProducts />} />
  <Route path="products/on-sale" element={<AdminSaleProducts />} />
  <Route path="/admin/products/make-sale" element={<AdminMakeSale />} />
  <Route path="/admin/products/remove-sale" element={<AdminRemoveSale />} />

  <Route path="abandoned-carts/:id" element={<AdminAbandonedCartDetails />} />
  
  <Route path="discounts/create-bulk-discount/:id" element={<AdminCreateBulkDiscount />} />

<Route path="abandoned-carts" element={<AdminAbandonedCarts />} />

  <Route path="users-activity" element={<AdminUserActivity />} />

  <Route
  path="/admin/email-template-manager"
  element={
    <ProtectedAdminRoute requiredPermission="can_manage_marketing">
      <AdminEmailTemplateManager />
    </ProtectedAdminRoute>
  }
/>


</Route>

      </Routes>
    </Router>
  );
}

export default App;
